import {
  ArrowsExpandIcon,
  ExternalLinkIcon,
  InformationCircleIcon,
} from '@heroicons/react/outline';
import {
  ActivityType,
  AssessmentSchemaType,
  ProgramActivityDataFragment,
} from '../../generated/graphql';
import { SvgIconComponent } from '../types/svgs';

export const programActivityTitle = (
  programActivity: ProgramActivityDataFragment,
): string => {
  switch (programActivity.activityType) {
    case ActivityType.Action:
      return 'Action';
    case ActivityType.Assessment:
      const isForm =
        programActivity.programAssessment!.assessment!.schemaType ===
        AssessmentSchemaType.Custom;
      return isForm
        ? programActivity.programAssessment!.assessment!.name
        : `${programActivity.programAssessment!.assessment!.name}${
            programActivity.programAssessment!.assessment!.shortName
              ? ` (${programActivity.programAssessment!.assessment!.shortName})`
              : ''
          }`;
    case ActivityType.Audio:
      return programActivity.activityAudio!.title;
    case ActivityType.Image:
      return programActivity.activityImage!.title;
    case ActivityType.Link:
      return programActivity.activityLink!.title;
    case ActivityType.Pdf:
    case ActivityType.PdfForm:
    case ActivityType.Esign:
      return programActivity.activityPDF!.title;
    case ActivityType.Text:
      return programActivity.activityText!.title;
    default:
      return programActivity.name;
  }
};

export const programActivityOpen = (
  programActivity: ProgramActivityDataFragment,
  onAssessmentOpen?: (programAssessmentId: string) => void,
  onTextOpen?: (
    activityText: ProgramActivityDataFragment['activityText'],
  ) => void,
): {
  onOpen?: () => void;
  openText?: string;
  openIcon?: SvgIconComponent;
  secondaryOnOpen?: () => void;
  secondaryOpenText?: string;
  secondaryOpenIcon?: SvgIconComponent;
} => {
  switch (programActivity.activityType) {
    case ActivityType.Esign:
      if (programActivity.pdfSubmission?.media?.url) {
        return {
          onOpen: () => {
            window.open(
              programActivity.pdfSubmission?.media?.url,
              '_blank',
              'noopener,noreferrer',
            );
          },
          openText: 'Open signed submission in new tab',
          openIcon: ExternalLinkIcon,
        };
      } else {
        return {
          onOpen: () => {
            window.open(
              programActivity.activityPDF?.media.url,
              '_blank',
              'noopener,noreferrer',
            );
          },
          openText: 'Open original document in new tab',
          openIcon: ExternalLinkIcon,
        };
      }
    case ActivityType.Image:
      return {
        onOpen: () => {
          window.open(
            programActivity.activityImage?.media.url,
            '_blank',
            'noopener,noreferrer',
          );
        },
        openText: 'View in new tab',
        openIcon: ExternalLinkIcon,
      };
    case ActivityType.Link:
      return {
        onOpen: () => {
          window.open(
            programActivity.activityLink?.url,
            '_blank',
            'noopener,noreferrer',
          );
        },
        openText: 'Open in new tab',
        openIcon: ExternalLinkIcon,
      };
    case ActivityType.Pdf:
      return {
        onOpen: () => {
          window.open(
            programActivity.activityPDF?.media.url,
            '_blank',
            'noopener,noreferrer',
          );
        },
        openText: 'Open in new tab',
        openIcon: ExternalLinkIcon,
      };
    case ActivityType.PdfForm:
      if (programActivity.pdfSubmission?.media?.url) {
        return {
          onOpen: () => {
            window.open(
              programActivity.pdfSubmission?.media?.url,
              '_blank',
              'noopener,noreferrer',
            );
          },
          openText: 'Open submission in new tab',
          openIcon: ExternalLinkIcon,
          secondaryOnOpen: () => {
            window.open(
              programActivity.activityPDF?.media.url,
              '_blank',
              'noopener,noreferrer',
            );
          },
          secondaryOpenText: 'Open original in new tab',
          secondaryOpenIcon: ExternalLinkIcon,
        };
      } else {
        return {
          onOpen: () => {
            window.open(
              programActivity.activityPDF?.media.url,
              '_blank',
              'noopener,noreferrer',
            );
          },
          openText: 'Open in new tab',
          openIcon: ExternalLinkIcon,
        };
      }
    case ActivityType.Assessment:
      if (
        onAssessmentOpen &&
        programActivity.programAssessment &&
        programActivity.programAssessment.assessment.schemaType !==
          AssessmentSchemaType.Custom
      ) {
        return {
          secondaryOnOpen: onAssessmentOpen.bind(
            null,
            programActivity.programAssessment.id,
          ),
          secondaryOpenText: 'See assessment details',
          secondaryOpenIcon: InformationCircleIcon,
        };
      }
      return {
        secondaryOnOpen: undefined,
        secondaryOpenIcon: undefined,
        secondaryOpenText: undefined,
      };
    case ActivityType.Text:
      return {
        onOpen: onTextOpen?.bind(null, programActivity.activityText),
        openText: 'Open text document',
        openIcon: ArrowsExpandIcon,
      };
    case ActivityType.Action:
    default:
      return { onOpen: undefined, openText: undefined };
  }
};

/**
 * Check if a program activity is a form with signature users
 */
export function isProgramActivityMultiSignatureForm(
  programActivity: ProgramActivityDataFragment,
): boolean {
  return (
    programActivity.activityType === ActivityType.Assessment &&
    programActivity.programAssessment?.programAssessmentSignatureUsers?.length >
      0
  );
}

/**
 * Guards for program-activity completion / dismissal actions.
 *
 * WEB-N / WEB-G ("Activity already completed" / "Activity has been voided"):
 * the `completeProgramActivity` mutation surfaces a backend guard error when a
 * request lands after the activity has already reached a terminal state
 * (completed or voided). The most common cause is a double-click while a
 * request is in-flight, so we drop the second submit entirely whenever a
 * related mutation is already loading.
 */

/**
 * Backend guard messages that mean the activity is already in a terminal
 * state by the time a (usually duplicate / late) request lands. These are
 * expected races, not application defects.
 */
const PROGRAM_ACTIVITY_ACTIONED_MESSAGES = [
  'Activity already completed',
  'Activity has been voided',
];

/**
 * Returns `true` if the given error message is one of the backend's
 * "activity already actioned" guards. Used to treat a late/duplicate
 * completion request as expected rather than an unhandled error.
 */
export const isProgramActivityAlreadyActionedError = (
  errorMessage?: string,
): boolean =>
  typeof errorMessage === 'string' &&
  PROGRAM_ACTIVITY_ACTIONED_MESSAGES.some((message) =>
    errorMessage.includes(message),
  );

/**
 * Returns `true` only when an action should be allowed to proceed.
 *
 * @param programActivityId the id of the activity being acted on
 * @param isLoading         whether the related mutation is currently in-flight
 */
export const canSubmitProgramActivity = (
  programActivityId: string | undefined,
  isLoading: boolean,
): boolean => Boolean(programActivityId) && !isLoading;

/**
 * Derives a user-facing message for an activity-action failure. The
 * completion path has a known expected race ("Activity already completed" /
 * "Activity has been voided"), so it gets a friendlier, less alarming message
 * than a generic failure.
 */
export const getActivityActionErrorMessage = (
  action: 'complete' | 'dismiss',
  errorMessage?: string,
): string => {
  const isAlreadyActioned = isProgramActivityAlreadyActionedError(errorMessage);

  if (action === 'complete') {
    return isAlreadyActioned
      ? 'This activity has already been completed.'
      : 'Unable to mark this activity complete. It may have already been completed.';
  }

  return 'Unable to dismiss this activity. Please try again.';
};
